import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { download, getRecordings, getSummary, getTranscript, searchTranscript } from './api'
import { clearDeepLink, pickRecording, readDeepLink } from './deep-link'
import FullPlayer from './FullPlayer'
import Icon from './Icon'
import listeningCover from '../assets/listening-headphones.jpg'
import listeningPhoto from '../assets/listening-headphones.jpg'
import {
  availableYears,
  formatDuration,
  formatSize,
  groupByMonth,
  recordingTitle,
  recordingsForYear,
} from './recording-utils'
import type { Recording, SearchRecording, TabId, Transcript } from './types'

interface Props {
  token: string
  onLogout: () => void
}

const progressKey = (key: string) => `huaren-audio-progress:${key}`
const resumeKey = 'huaren-audio-resume'

interface ResumeState {
  key: string
  currentTime: number
}

const readProgress = (key: string) => {
  const value = Number(window.localStorage.getItem(progressKey(key)))
  return Number.isFinite(value) && value > 0 ? value : 0
}

const readResume = (): ResumeState | null => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(resumeKey) || 'null') as ResumeState | null
    return saved && typeof saved.key === 'string' && Number.isFinite(saved.currentTime) && saved.currentTime > 0 ? saved : null
  } catch {
    return null
  }
}

function Artwork({ size = 'normal', cover = false }: { size?: 'normal' | 'large'; cover?: boolean }) {
  return (
    <div className={`recording-artwork ${size} ${cover ? 'cover-artwork' : ''}`} style={cover ? { backgroundImage: `url(${listeningCover})` } : undefined} aria-hidden="true">
      {!cover && <><span className="artwork-glow" /><Icon name="wave" size={size === 'large' ? 36 : 24} /></>}
    </div>
  )
}

function DateLabel({ recording }: { recording: Recording }) {
  const date = new Date(recording.recordedAt ?? '')
  return <>{Number.isNaN(date.valueOf()) ? '华仁录音' : date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })}</>
}

function LibraryTitle({ recording }: { recording: Recording }) {
  const date = new Date(recording.recordedAt ?? '')
  if (Number.isNaN(date.valueOf())) return <>{recordingTitle(recording)}</>
  return <>华仁录音 · {date.getMonth() + 1} 月 {date.getDate()} 日</>
}

function FormattedSearchText({ value }: { value: string }) {
  const parts = value.split(/(<mark>[\s\S]*?<\/mark>)/gi)
  return <>{parts.map((part, index) => part.toLocaleLowerCase().startsWith('<mark>') && part.toLocaleLowerCase().endsWith('</mark>') ? <mark key={index}>{part.slice(6, -7)}</mark> : part)}</>
}

export default function AudioArchiveApp({ token, onLogout }: Props) {
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<TabId>('listen')
  const [year, setYear] = useState<string | undefined>()
  const [current, setCurrent] = useState<Recording | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speed, setSpeed] = useState(1)
  const [showPlayer, setShowPlayer] = useState(false)
  const [miniPlayerCollapsed, setMiniPlayerCollapsed] = useState(false)
  const [miniPlayerMenuOpen, setMiniPlayerMenuOpen] = useState(false)
  const [transcript, setTranscript] = useState<Transcript | null>(null)
  const [transcriptLoading, setTranscriptLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchRecording[]>([])
  const [searching, setSearching] = useState(false)
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null)
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false)
  const [summary, setSummary] = useState<string | null>(null)
  const [summaryError, setSummaryError] = useState('')
  const [resume, setResume] = useState<ResumeState | null>(null)
  const audio = useRef<HTMLAudioElement>(null)
  const searchInput = useRef<HTMLInputElement>(null)

  const years = useMemo(() => availableYears(recordings), [recordings])
  const visibleYear = year ?? years[0]
  const libraryRecordings = useMemo(() => recordingsForYear(recordings, visibleYear), [recordings, visibleYear])
  const groupedLibrary = useMemo(() => groupByMonth(libraryRecordings), [libraryRecordings])
  const resumeRecording = resume ? recordings.find(recording => recording.key === resume.key) ?? null : null
  const continueRecording = current ?? resumeRecording ?? recordings[0] ?? null
  const recentRecordings = useMemo(
    () => recordings.filter(recording => recording.key !== continueRecording?.key).slice(0, 2),
    [recordings, continueRecording],
  )
  const continueTime = current?.key === continueRecording?.key ? currentTime : resume?.key === continueRecording?.key ? resume.currentTime : 0
  const continueDuration = current?.key === continueRecording?.key ? duration || continueRecording?.durationSeconds || 0 : continueRecording?.durationSeconds || 0

  useEffect(() => {
    getRecordings(token)
      .then((items) => {
        setRecordings(items)
        setYear(availableYears(items)[0])
        const savedResume = readResume()
        setResume(savedResume && items.some(recording => recording.key === savedResume.key) ? savedResume : null)
      })
      .catch((reason: Error) => setError(reason.message || '资料库暂时无法加载。'))
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    if (!current) {
      setTranscript(null)
      return
    }
    setTranscriptLoading(true)
    getTranscript(token, current.name)
      .then(setTranscript)
      .catch(() => setTranscript({ text: '', segments: [], isTimed: false }))
      .finally(() => setTranscriptLoading(false))
  }, [current, token])

  useEffect(() => {
    if (!audio.current) return
    audio.current.playbackRate = speed
  }, [speed])

  useEffect(() => {
    if (!logoutConfirmOpen) return
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setLogoutConfirmOpen(false) }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [logoutConfirmOpen])

  const saveResume = (recording: Recording, time: number, totalDuration: number) => {
    if (time <= 5 || (totalDuration > 0 && time >= totalDuration - 30)) return
    const nextResume = { key: recording.key, currentTime: time }
    window.localStorage.setItem(resumeKey, JSON.stringify(nextResume))
    setResume(nextResume)
  }

  const playRecording = async (recording: Recording, startAt?: number, openPlayer = false, quietAutoplayBlock = false) => {
    const element = audio.current
    const changed = current?.key !== recording.key
    setCurrent(recording)
    setShowPlayer(openPlayer)
    setMiniPlayerCollapsed(false)
    setMiniPlayerMenuOpen(false)
    if (!element) return
    if (changed) {
      element.src = recording.streamUrl ?? ''
      element.load()
      const initialTime = startAt ?? (resume?.key === recording.key ? resume.currentTime : readProgress(recording.key))
      const setInitialTime = () => { element.currentTime = initialTime }
      element.addEventListener('loadedmetadata', setInitialTime, { once: true })
    } else if (typeof startAt === 'number') {
      element.currentTime = startAt
    }
    try {
      await element.play()
    } catch (reason) {
      // 自动播放被浏览器拦截不是故障：播放器已经打开，用户点播放键即可
      if (quietAutoplayBlock && reason instanceof DOMException && reason.name === 'NotAllowedError') return
      setError('音频暂时无法播放，请稍后再试。')
    }
  }

  // 深链（华仁谋士聊天里的"去听录音"/"听原声"）指定了日期（可选：哪一段、从第几秒）：录音加载完后直接打开播放器。
  // 从链接新开的页面没有用户手势，浏览器会拦截自动播放——播放器照常打开，由用户点一下播放键。
  const deepLinkHandled = useRef(false)
  useEffect(() => {
    if (loading || deepLinkHandled.current) return
    deepLinkHandled.current = true
    const link = readDeepLink()
    if (!link) return
    clearDeepLink()
    const match = pickRecording(recordings, link)
    if (!match) {
      setError(`没有找到 ${link.date} 的录音。`)
      return
    }
    void playRecording(match, link.startSec ?? undefined, true, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, recordings])

  const togglePlayback = async () => {
    if (!current) {
      if (recordings[0]) await playRecording(recordings[0])
      return
    }
    if (audio.current?.paused) await audio.current.play()
    else audio.current?.pause()
  }

  const collapseMiniPlayer = () => {
    audio.current?.pause()
    setMiniPlayerMenuOpen(false)
    setMiniPlayerCollapsed(true)
  }

  const endListening = () => {
    const recording = current
    audio.current?.pause()
    if (audio.current) {
      audio.current.removeAttribute('src')
      audio.current.load()
    }
    if (recording) window.localStorage.removeItem(progressKey(recording.key))
    window.localStorage.removeItem(resumeKey)
    setPlaying(false)
    setShowPlayer(false)
    setMiniPlayerCollapsed(false)
    setMiniPlayerMenuOpen(false)
    setCurrent(null)
    setCurrentTime(0)
    setDuration(0)
    setResume(null)
    setTranscript(null)
    setSummary(null)
    setSummaryError('')
  }

  const seek = (time: number) => {
    const element = audio.current
    const upperBound = duration || current?.durationSeconds || element?.duration || Number.MAX_SAFE_INTEGER
    const nextTime = Math.max(0, Math.min(upperBound, time))
    // Update immediately while the thumb is being dragged. Waiting for the media
    // element's next timeupdate makes the controlled range snap back under a finger.
    setCurrentTime(nextTime)
    if (current) {
      window.localStorage.setItem(progressKey(current.key), String(nextTime))
      saveResume(current, nextTime, upperBound)
    }
    if (element) element.currentTime = nextTime
  }

  const runSearch = async (event?: FormEvent) => {
    event?.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) return
    setSearching(true)
    setSearchedQuery(trimmed)
    try {
      setSearchResults((await searchTranscript(token, trimmed)).recordings)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '搜索暂时不可用。')
    } finally {
      setSearching(false)
    }
  }

  const openSummary = async () => {
    if (!current?.hasSummary) return
    setSummaryError('')
    setSummary('正在整理 AI 总结…')
    try {
      setSummary(await getSummary(token, current.name))
    } catch {
      setSummary(null)
      setSummaryError('暂时无法读取这份 AI 总结。')
    }
  }

  const downloadTranscript = () => {
    if (!current) return
    const base = current.name.replace(/\.mp3$/i, '')
    download(token, `transcriptions/${base}.txt`, `${recordingTitle(current)} 文字稿.txt`).catch(() => setError('文字稿暂时无法下载。'))
  }

  if (loading) return <main className="archive-loading" role="status" aria-live="polite">正在整理你的资料库…</main>
  if (error && recordings.length === 0) return <main className="archive-error">{error}<button onClick={() => window.location.reload()}>重新载入</button></main>

  return (
    <main className="archive-app">
      <audio
        ref={audio}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || current?.durationSeconds || 0)}
        onTimeUpdate={(event) => {
          const value = event.currentTarget.currentTime
          setCurrentTime(value)
          if (current) {
            window.localStorage.setItem(progressKey(current.key), String(value))
            saveResume(current, value, event.currentTarget.duration || duration || current.durationSeconds || 0)
          }
        }}
        onEnded={() => {
          setPlaying(false)
          if (current) window.localStorage.removeItem(progressKey(current.key))
          window.localStorage.removeItem(resumeKey)
          setResume(null)
          setCurrentTime(0)
        }}
        onError={() => setError('音频暂时无法播放，请稍后再试。')}
      />

      {error && <div className="toast" role="status">{error}<button onClick={() => setError('')} aria-label="关闭提示">×</button></div>}

      <section className={`archive-page ${tab === 'listen' ? 'active' : ''}`} aria-hidden={tab !== 'listen'}>
        <header className="app-header">
          <h1>录音</h1>
          <button className="account-button" onClick={() => setLogoutConfirmOpen(true)} aria-label="账户与退出登录"><Icon name="person" /></button>
        </header>

        <section className="listening-welcome" aria-label="你的收听空间"><div><span className="welcome-label">给自己一点收听时间</span><h2>听一点，<br />让想法多一点。</h2><p>从熟悉的声音里，发现新的灵感。</p></div><img src={listeningPhoto} alt="柔和日光下，浅绿色耳机放在一本书上" width="1200" height="800" /></section>

        {continueRecording && <section className="continue-card">
          <div className="section-caption">继续收听</div>
          <div className="continue-body">
            <Artwork size="large" cover />
            <div>
              <p className="eyebrow"><DateLabel recording={continueRecording} /></p>
              <h2>{recordingTitle(continueRecording)}</h2>
              <div className="continue-progress"><span style={{ width: continueTime && continueDuration ? `${(continueTime / continueDuration) * 100}%` : '0%' }} /></div>
              <p className="continue-time">{continueTime > 0 ? formatDuration(continueTime) : '从头收听'}<span>{continueDuration ? `还剩 ${formatDuration(Math.max(0, continueDuration - continueTime))}` : ''}</span></p>
            </div>
            <button className="continue-play" onClick={() => playRecording(continueRecording, undefined, true)} aria-label="继续播放"><Icon name="play" size={29} /></button>
          </div>
        </section>}

        <section className="recent-section">
          <div className="section-heading"><h2>最近加入</h2><button onClick={() => setTab('library')}>查看全部</button></div>
          <div className="recent-list">
            {recentRecordings.map((recording) => <button className="recent-row" key={recording.key} onClick={() => playRecording(recording, undefined, true)}>
              <Icon name="clock" size={20} />
              <i className="desktop-recording-thumbnail" aria-hidden="true" />
              <span><strong>{recordingTitle(recording)}</strong></span>
              <small>{formatDuration(recording.durationSeconds) === '—' ? formatSize(recording.size) : formatDuration(recording.durationSeconds)}</small>
              <Icon name="chevron-right" />
            </button>)}
          </div>
        </section>

        <section className="browse-section">
          <div className="section-heading"><h2>按年份浏览</h2><button onClick={() => setTab('library')}>查看资料库 <Icon name="chevron-right" size={18} /></button></div>
          <div className="year-pills">{years.slice(0, 3).map((item) => <button key={item} onClick={() => { setYear(item); setTab('library') }}><Icon name="calendar" size={21} /><span>{item}</span></button>)}</div>
        </section>
      </section>

      <section className={`archive-page library-page ${tab === 'library' ? 'active' : ''}`} aria-hidden={tab !== 'library'}>
        <header className="app-header"><p>华仁音频库</p><h1>资料库</h1><span className="header-count">{recordings.length} 份录音</span></header>
        <div className="year-rail" aria-label="按年份浏览">
          {years.map((item) => <button className={item === visibleYear ? 'selected' : ''} key={item} onClick={() => setYear(item)}><strong>{item}</strong><span>{recordingsForYear(recordings, item).length} 份录音</span></button>)}
        </div>
        <div className="library-list">
          {groupedLibrary.map(([month, items]) => <section className="month-group" key={month}><h2>{month}</h2><div className="month-card">{items.map((recording) => {
            const date = new Date(recording.recordedAt ?? '')
            const isCurrent = current?.key === recording.key
            return <article className={`library-row ${isCurrent ? 'is-current' : ''}`} key={recording.key}>
              <button className="date-tile" onClick={() => playRecording(recording, undefined, true)}><b>{date.getDate() || '—'}</b><span>{Number.isNaN(date.valueOf()) ? '—' : `${date.getMonth() + 1}月`}</span></button>
              <button className="library-copy" onClick={() => playRecording(recording, undefined, true)}><strong>{isCurrent && playing && <Icon name="wave" size={22} />}<LibraryTitle recording={recording} /></strong><small>{isCurrent && playing ? '正在播放' : formatDuration(recording.durationSeconds) === '—' ? formatSize(recording.size) : `${formatDuration(recording.durationSeconds)} · ${formatSize(recording.size)}`}</small>{isCurrent && <span className="library-progress"><i style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }} /></span>}</button>
              <button className="round-play" onClick={() => isCurrent ? togglePlayback() : playRecording(recording)} aria-label={`${isCurrent && playing ? '暂停' : '播放'} ${recordingTitle(recording)}`}><Icon name={isCurrent && playing ? 'pause' : 'play'} size={14} /></button>
            </article>
          })}</div></section>)}
          {libraryRecordings.length === 0 && <p className="empty-state">这一年还没有收录的录音。</p>}
        </div>
      </section>

      <section className={`archive-page search-page ${tab === 'search' ? 'active' : ''}`} aria-hidden={tab !== 'search'}>
        <header className="app-header"><p>华仁音频库</p><h1>搜索</h1><span className="header-count">文字稿</span></header>
        <div className="search-bar-row"><form className="search-field" onSubmit={runSearch}>
          <Icon name="search" size={24} />
          <input ref={searchInput} value={query} onChange={(event) => { setQuery(event.target.value); setSearchResults([]); setSearchedQuery(null) }} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void runSearch() } }} placeholder="搜索文字稿" aria-label="搜索文字稿" />
          {query && <button type="button" className="clear-search" onClick={() => { setQuery(''); setSearchResults([]); setSearchedQuery(null); searchInput.current?.focus() }}>×</button>}
        </form><button className="search-submit" type="button" onClick={() => { void runSearch() }} disabled={!query.trim() || searching}>搜索</button></div>
        {!query && searchResults.length === 0 && <section className="search-intro"><div className="search-orb"><Icon name="search" size={26} /></div><h2>从说过的话开始</h2><p>搜索全部录音的文字稿，点击结果即可回到当时的声音。</p></section>}
        {query && !searchedQuery && !searching && searchResults.length === 0 && <p className="search-hint">按回车搜索文字稿</p>}
        {searching && <section className="searching-state" role="status" aria-live="polite"><div className="searching-orbit" aria-hidden="true"><span className="orbit-ring ring-one" /><span className="orbit-ring ring-two" /><Icon name="search" size={27} /></div><p>正在搜索文字稿<span className="searching-dots"><i /><i /><i /></span></p><small>正在从全部录音中寻找相关内容</small></section>}
        {searchedQuery && !searching && searchResults.length === 0 && <section className="search-empty" role="status" aria-live="polite"><div className="search-empty-orb" aria-hidden="true"><span /><Icon name="search" size={27} /></div><h2>没有找到“{searchedQuery}”</h2><p>这次没有在文字稿中找到相关内容。<br />换个说法，或试试更短的关键词。</p><button type="button" onClick={() => { setQuery(''); setSearchedQuery(null); searchInput.current?.focus() }}>清除关键词</button></section>}
        {searchResults.length > 0 && <div className="search-results"><p className="result-count">找到 {searchResults.reduce((total, item) => total + item.matches.length, 0)} 处文字稿命中</p>{searchResults.map(({ audio: recording, matches }) => <section className="search-recording" key={recording.key}><div className="search-recording-head"><div><strong>{recordingTitle(recording)}</strong><small>{matches.length} 处命中</small></div></div>{matches.map((match, index) => <button key={`${match.startTime}-${index}`} className="match-row" onClick={() => playRecording(recording, match.startTime ?? 0, true)}><Icon name="document" size={39} /><p><FormattedSearchText value={match.formatText} /><small>文字稿 · 点击打开录音</small></p></button>)}</section>)}</div>}
      </section>

      {current && !showPlayer && <div className={`mini-player ${miniPlayerCollapsed ? 'collapsed' : ''}`}>
        {miniPlayerMenuOpen && <button className="mini-player-menu-backdrop" aria-label="关闭播放器菜单" onClick={() => setMiniPlayerMenuOpen(false)} />}
        {miniPlayerCollapsed ? <button className="mini-player-resume" onClick={() => setMiniPlayerCollapsed(false)} aria-label="展开继续收听"><Artwork cover /><span><strong>继续收听</strong><small>{recordingTitle(current)}</small></span><Icon name="play" size={18} /></button> : <><button className="mini-player-open" onClick={() => setShowPlayer(true)} aria-label="打开播放器"><Artwork cover /><span><strong>{recordingTitle(current)}</strong><small>{formatDuration(currentTime)}{duration ? ` / ${formatDuration(duration)}` : ''}</small></span></button><button className="mini-player-toggle" onClick={togglePlayback} aria-label={playing ? '暂停播放' : '开始播放'}><Icon name={playing ? 'pause' : 'play'} size={27} /></button><div className="mini-player-actions"><button className="mini-player-more" onClick={() => setMiniPlayerMenuOpen(open => !open)} aria-label="播放器更多操作" aria-expanded={miniPlayerMenuOpen}><Icon name="more" size={23} /></button>{miniPlayerMenuOpen && <div className="mini-player-menu" role="menu"><button role="menuitem" onClick={collapseMiniPlayer}><Icon name="collapse" size={20} /><span><strong>收起播放器</strong><small>暂停播放，保留继续收听入口</small></span></button><button className="end-listening" role="menuitem" onClick={endListening}><Icon name="stop" size={20} /><span><strong>结束本次收听</strong><small>停止播放并清除播放进度</small></span></button></div>}</div></>}</div>}

      <nav className="tab-bar" aria-label="主导航">
        <div className="desktop-brand"><span><Icon name="wave" size={27} /></span><strong>华仁音频库</strong><small>留住声音，常听常新</small></div>
        <p className="desktop-nav-caption">我的音频空间</p>
        {([{ id: 'listen', icon: 'headphones', label: '录音' }, { id: 'library', icon: 'archive', label: '资料库' }, { id: 'search', icon: 'search', label: '搜索' }] as const).map((item) => <button key={item.id} aria-current={tab === item.id ? 'page' : undefined} className={tab === item.id ? 'selected' : ''} onClick={() => { setTab(item.id); setShowPlayer(false); setSummary(null); setSummaryError('') }}><Icon name={item.icon} /><span>{item.label}</span></button>)}
        <div className="desktop-library-note"><Icon name="library" size={23} /><strong>每一次重听，都有新发现</strong><p>{recordings.length} 份录音，陪你慢慢回顾。<br />从声音中拾回值得留下的思考。</p></div>
      </nav>

      {showPlayer && current && <FullPlayer
        recording={current}
        transcript={transcript}
        transcriptLoading={transcriptLoading}
        currentTime={currentTime}
        duration={duration || current.durationSeconds || 0}
        playing={playing}
        speed={speed}
        summary={summary}
        summaryError={summaryError}
        onDismiss={() => { setShowPlayer(false); setSummary(null); setSummaryError('') }}
        onTogglePlay={togglePlayback}
        onSeek={seek}
        onSkip={(seconds) => seek(currentTime + seconds)}
        onSpeed={setSpeed}
        onShowSummary={openSummary}
        onDownloadAudio={() => download(token, current.key, `${recordingTitle(current)}.mp3`).catch(() => setError('音频暂时无法下载。'))}
        onDownloadTranscript={downloadTranscript}
      />}

      {logoutConfirmOpen && <section className="logout-confirm" role="dialog" aria-modal="true" aria-labelledby="logout-confirm-title"><div className="logout-confirm-card"><div className="logout-confirm-icon" aria-hidden="true"><Icon name="person" size={29} /></div><p>华仁音频库</p><h2 id="logout-confirm-title">确定要退出登录吗？</h2><span>退出后可随时再次登录。</span><div className="logout-confirm-actions"><button type="button" onClick={() => setLogoutConfirmOpen(false)}>取消</button><button type="button" className="logout-confirm-action" onClick={onLogout}>退出登录</button></div></div></section>}

      {(summary || summaryError) && !showPlayer && <div className="summary-overlay" role="dialog" aria-modal="true" aria-label="AI 总结"><button className="summary-backdrop" onClick={() => { setSummary(null); setSummaryError('') }} aria-label="关闭" /><section className="summary-card"><header><div><p>AI 总结</p><h2>{current ? recordingTitle(current) : ''}</h2></div><button onClick={() => { setSummary(null); setSummaryError('') }} aria-label="关闭">×</button></header>{summaryError ? <p className="empty-state">{summaryError}</p> : <article className="markdown-summary">{summary === '正在整理 AI 总结…' ? <p className="summary-loading">正在整理 AI 总结…</p> : <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary ?? ''}</ReactMarkdown>}</article>}</section></div>}
    </main>
  )
}
